'use client';

import { Checkbox } from '@/features/uiKit/Checkbox';
import { Link } from '@/features/uiKit/Link';
import { Stack } from '@/features/uiKit/Stack';
import type { SxProps } from '@/features/uiKit/sxProps';
import { Text } from '@/features/uiKit/Text';
import { MarkdownToJSX, default as MarkdownTool } from 'markdown-to-jsx';
import React from 'react';

type MdVariantVariant = 'small' | 'normal' | 'blog';
export interface MarkdownProps {
  children: string;
  variant?: MdVariantVariant;
  /** Room text is written by members. Keep markdown, drop raw HTML. */
  safe?: boolean;
  onWordClick?: (word: string, element: HTMLElement) => void;
}

const markdownComponents: MarkdownToJSX.Overrides = {
  h1: ({ children }) => (
    <Text as="h1" variant="title">
      {children}
    </Text>
  ),
  h2: ({ children }) => (
    <Text as="h2" variant="heading">
      {children}
    </Text>
  ),
  h3: ({ children }) => (
    <Text as="h3" variant="heading">
      {children}
    </Text>
  ),
  h4: ({ children }) => (
    <Text as="h4" variant="heading">
      {children}
    </Text>
  ),
  h5: ({ children }) => (
    <Text as="h4" variant="heading">
      {children}
    </Text>
  ),
  h6: ({ children }) => (
    <Text as="h4" variant="heading">
      {children}
    </Text>
  ),

  p: ({ children }) => <Text>{children}</Text>,
  span: ({ children }) => <Text as="span">{children}</Text>,
  a: ({ href, children }) => (
    <Link href={href} target="_blank">
      {children}
    </Link>
  ),
  ul: ({ children }) => (
    <ul
      style={{
        padding: '0 0 0 20px',
        margin: '5px 0',
      }}
    >
      {children}
    </ul>
  ),
  ol: ({ children }) => (
    <ol
      style={{
        padding: '0 0 0 20px',
        margin: '5px 0',
      }}
    >
      {children}
    </ol>
  ),
  li: ({ children }) => <Text as="li">{children}</Text>,
  input: ({ checked }) => (
    <Checkbox
      checked={checked}
      disabled
      sx={{
        padding: '0',
      }}
    />
  ),
  small: ({ children }) => (
    <Text as="span" variant="small" color="secondary">
      {children}
    </Text>
  ),
  em: ({ children }) => <em>{children}</em>,
  strong: ({ children }) => <strong>{children}</strong>,
  blockquote: ({ children }) => (
    <Stack
      component={'blockquote'}
      sx={{
        margin: '10px 10px 10px 0',
        padding: '5px 10px 5px 15px',
        borderLeft: '4px solid rgba(0, 0, 0, 0.1)',
      }}
    >
      {children}
    </Stack>
  ),
  pre: ({ children }) => <pre>{children}</pre>,
  code: ({ children }) => <code>{children}</code>,
  thead: ({ children }) => <thead>{children}</thead>,
  tbody: ({ children }) => <tbody>{children}</tbody>,
  tr: ({ children }) => <tr>{children}</tr>,
  th: ({ children }) => (
    <th
      style={{
        textAlign: 'left',
        padding: '8px 10px',
        borderBottom: '1px solid rgba(0, 0, 0, 0.15)',
        fontWeight: 650,
      }}
    >
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td
      style={{
        textAlign: 'left',
        padding: '8px 10px',
        borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
        verticalAlign: 'top',
      }}
    >
      {children}
    </td>
  ),
  table: ({ children }) => (
    <div style={{ overflowX: 'auto', width: '100%', margin: '12px 0 20px' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '1rem' }}>
        {children}
      </table>
    </div>
  ),
  img: (props) => <img {...props} style={{ maxWidth: '90%' }} />,
};

const markdownComponentsBlog: MarkdownToJSX.Overrides = {
  ...markdownComponents,
  blockquote: ({ children }) => (
    <blockquote
      style={{
        margin: '10px 10px 10px 0',
        padding: '5px 10px 5px 15px',
        borderLeft: '4px solid rgba(0, 0, 0, 0.1)',
      }}
    >
      {children}
    </blockquote>
  ),
  img: (props) => <img {...props} style={{ maxWidth: '100%' }} />,
};

const styleVariationMap: Record<MdVariantVariant, MarkdownToJSX.Overrides> = {
  small: markdownComponents,
  normal: markdownComponents,
  blog: markdownComponentsBlog,
};

/** Vertical rhythm between prose blocks. Text components carry no margins of their own. */
const blockSpacing: Record<MdVariantVariant, SxProps> = {
  small: {
    '& > p': { padding: '2px 0' },
    '& > :is(h1, h2, h3, h4)': { paddingTop: '20px', paddingBottom: '4px' },
  },
  normal: {
    '& > p': { padding: '3px 0 5px 0' },
    '& > :is(h1, h2, h3, h4)': { paddingTop: '20px', paddingBottom: '4px' },
  },
  blog: {
    '& > p': { padding: '5px 0 10px 0' },
    '& > :is(h1, h2, h3, h4)': { paddingTop: '28px', paddingBottom: '6px' },
  },
};

export const Markdown: React.FC<MarkdownProps> = ({ children, onWordClick, variant, safe }) => {
  const variantToUse = variant || 'normal';
  const styleComponents = styleVariationMap[variantToUse];

  return (
    <Stack
      sx={{
        ...blockSpacing[variantToUse],
        '.conversation-word': onWordClick
          ? {
              ':hover': {
                cursor: 'pointer',
                borderBottom: '1px dashed #fff',
              },
            }
          : {},
      }}
      onMouseDown={
        onWordClick
          ? (e) => {
              const target = e.target as HTMLElement;
              if (target.classList.contains('conversation-word')) {
                const word = target.textContent || '';
                const element = target;
                onWordClick(word.trim(), element);
              }
            }
          : undefined
      }
    >
      <MarkdownTool
        options={{
          overrides: styleComponents,
          disableParsingRawHTML: Boolean(safe),
          wrapper: React.Fragment,
        }}
      >
        {children}
      </MarkdownTool>
    </Stack>
  );
};
